import classNames from 'classnames';
import { useToProductTypeLabel } from 'components/common-form/builder/form/node/field/ProductListField';
import type { IntlKey } from 'locales';
import type { ProductVariation } from 'models/productsReferential';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { AppState } from 'state/state.model';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { getActivatedAssetTypeFilter } from 'state/workspaces/workspaces/workspaces.selector';
import { optionalMap } from 'utils/optional';

import { ProductCategoryItem } from './ProductCategoryItem';

interface ProductTypeSelector {
  className?: string;
  workspace: WorkspaceWithProductSelector;
  availableProductVariations: readonly ProductVariation[];
  selectedProductType?: string;
  onProductTypeSelected(productType: string): void;
}

export const ProductTypeSelector: FC<ProductTypeSelector> = ({
  className,
  workspace,
  availableProductVariations,
  selectedProductType,
  onProductTypeSelected,
}) => {
  const activatedAssetTypeFilter = useSelector((state: AppState) => getActivatedAssetTypeFilter(state, workspace));
  const productTypeOptions = useGetProductTypeOptions(availableProductVariations, activatedAssetTypeFilter);
  return (
    <div data-e2e="product-type-selector" className={classNames('border-end overflow-auto', className)}>
      {productTypeOptions.length > 0 ? (
        productTypeOptions.map(({ label, value }) => (
          <ProductCategoryItem
            key={value}
            e2e="product-type-selector-item"
            className="ps-3 pe-0"
            isItemSelected={selectedProductType === value}
            onItemClick={() => onProductTypeSelected(value)}
          >
            <span className="text-wrap text-start">{label}</span>
            <span className="icon icon-sm text-muted mx-2">keyboard_arrow_right</span>
          </ProductCategoryItem>
        ))
      ) : (
        <div className="h-100 d-flex flex-column justify-content-center align-items-center px-4">
          <span className="icon icon-outlined icon-lg text-secondary">group_work</span>
          <p className="text-center text-secondary">
            <FormattedMessage id="productSelector.available.productTypes" />
          </p>
        </div>
      )}
    </div>
  );
};

const useGetProductTypeOptions = (productVariations: readonly ProductVariation[], activatedAssetTypeFilter: string) => {
  const { formatMessage } = useIntl();
  const toLabel = optionalMap(useToProductTypeLabel());

  return useMemo(() => {
    const filteredProductTypes = productVariations.filter(
      ({ assetTypes }) =>
        assetTypes?.some(assetType => activatedAssetTypeFilter === assetType) || activatedAssetTypeFilter === 'All',
    );

    const distinctProductTypeOptions = filteredProductTypes
      .filter(({ productType }, pos, all) => all.findIndex(product => product.productType === productType) === pos)
      .map(({ productType, commercialProductType }) => ({
        label:
          toLabel(commercialProductType) ??
          formatMessage({
            id: `quote.products.${productType}` as IntlKey,
            defaultMessage: productType,
          }),
        value: productType,
      }));

    return distinctProductTypeOptions;
  }, [activatedAssetTypeFilter, formatMessage, productVariations, toLabel]);
};
